<script setup>
import { Head, Link, useForm } from '@inertiajs/inertia-vue3';
import { User, Mail, Lock } from 'lucide-vue-next';
import JetValidationErrors from '@/Jetstream/ValidationErrors.vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Checkbox } from '@/Components/ui/checkbox';

const form = useForm({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms: false,
});

const submit = () => {
    form.post(route('register'), {
        onFinish: () => form.reset('password', 'password_confirmation'),
    });
};
</script>

<template>
    <Head title="Đăng ký tài khoản" />

    <div class="flex min-h-screen items-center justify-center bg-muted px-4 py-12">
        <Card class="w-full max-w-md">
            <CardHeader class="space-y-2 text-center">
                <CardTitle class="text-3xl">
                    <span class="font-bold">Đông Tây</span> ERP
                </CardTitle>
                <CardDescription>Tạo tài khoản mới</CardDescription>
            </CardHeader>

            <CardContent>
                <JetValidationErrors class="mb-4" />

                <form class="space-y-4" @submit.prevent="submit">
                    <div class="space-y-2">
                        <Label for="name">Họ tên</Label>
                        <div class="relative">
                            <User class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="name"
                                v-model="form.name"
                                type="text"
                                class="pl-10"
                                placeholder="Nguyễn Văn A"
                                required
                                autofocus
                                autocomplete="name"
                            />
                        </div>
                    </div>

                    <div class="space-y-2">
                        <Label for="email">Email</Label>
                        <div class="relative">
                            <Mail class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="email"
                                v-model="form.email"
                                type="email"
                                class="pl-10"
                                placeholder="email@dongtay.vn"
                                required
                            />
                        </div>
                    </div>

                    <div class="space-y-2">
                        <Label for="password">Mật khẩu</Label>
                        <div class="relative">
                            <Lock class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="password"
                                v-model="form.password"
                                type="password"
                                class="pl-10"
                                placeholder="••••••••"
                                required
                                autocomplete="new-password"
                            />
                        </div>
                    </div>

                    <div class="space-y-2">
                        <Label for="password_confirmation">Xác nhận mật khẩu</Label>
                        <div class="relative">
                            <Lock class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                type="password"
                                class="pl-10"
                                placeholder="••••••••"
                                required
                                autocomplete="new-password"
                            />
                        </div>
                    </div>

                    <div v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature" class="flex items-start gap-2">
                        <Checkbox id="terms" v-model:checked="form.terms" class="mt-0.5" />
                        <Label for="terms" class="cursor-pointer font-normal text-muted-foreground">
                            Tôi đồng ý với
                            <a target="_blank" :href="route('terms.show')" class="text-primary hover:underline">Điều khoản dịch vụ</a>
                            và
                            <a target="_blank" :href="route('policy.show')" class="text-primary hover:underline">Chính sách bảo mật</a>
                        </Label>
                    </div>

                    <Button
                        type="submit"
                        class="w-full"
                        :class="{ 'opacity-50': form.processing }"
                        :disabled="form.processing"
                    >
                        Đăng ký
                    </Button>
                </form>

                <div class="mt-6 text-center text-sm text-muted-foreground">
                    Đã có tài khoản?
                    <Link :href="route('login')" class="text-primary hover:underline">Đăng nhập</Link>
                </div>
            </CardContent>
        </Card>
    </div>
</template>
